import React from 'react';
import { SimpleModal } from '../SimpleModal';
import { Button } from '../Button';
import { cn } from '../../utils';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmButtonVariant?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'ghost' | 'link';
  cancelButtonVariant?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'ghost' | 'link';
  isLoading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  className?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonVariant = 'primary',
  cancelButtonVariant = 'ghost',
  isLoading = false,
  size = 'sm',
  closeOnBackdrop = true,
  closeOnEsc = true,
  className,
}) => {
  const [isConfirming, setIsConfirming] = React.useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling should be done by the consumer
      console.error('Confirm action failed:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    if (!isConfirming && !isLoading) {
      onClose();
    }
  };

  const footer = (
    <div className="flex gap-2 justify-end">
      <Button
        variant={cancelButtonVariant}
        onClick={handleCancel}
        disabled={isConfirming || isLoading}
      >
        {cancelText}
      </Button>
      <Button
        variant={confirmButtonVariant}
        onClick={handleConfirm}
        loading={isConfirming || isLoading}
        disabled={isConfirming || isLoading}
      >
        {confirmText}
      </Button>
    </div>
  );

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      size={size}
      footer={footer}
      closeOnBackdrop={closeOnBackdrop && !isConfirming && !isLoading}
      closeOnEsc={closeOnEsc && !isConfirming && !isLoading}
      showCloseButton={!isConfirming && !isLoading}
      className={cn('confirm-dialog', className)}
    >
      {typeof message === 'string' ? (
        <p className="text-base">{message}</p>
      ) : (
        message
      )}
    </SimpleModal>
  );
};